import React, { useState, useEffect } from 'react';
import { useUser } from '../userContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { BeatLoader } from 'react-spinners';

export default function Company() {
  const { user, businessProfile, isOwner, userProfile } = useUser();
  const [options, setOptions] = useState({
    name: '',
    description: ''
  });
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (businessProfile) {
      setOptions({
        name: businessProfile.name || '',
        description: businessProfile.description || ''
      });
    }
  }, [businessProfile]);

  if (loading || !businessProfile) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOptions({
      ...options,
      [name]: value
    });
    setUnsavedChanges(true);
    setSaveSuccess(false); // Reset success message on change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (businessProfile && user) {
      setLoading(true);
      const businessDocRef = doc(db, 'company', businessProfile.id);
      
      const updateData = {
        name: options.name,
        description: options.description
      };
      
      try {
        await updateDoc(businessDocRef, updateData);
        setUnsavedChanges(false);
        setSaveSuccess(true);
      } catch (error) {
        console.error("Error updating document: ", error);
      } finally {
        setLoading(false);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    }
  };



  // Check if user is agency managing client
  const canManageCompany = () => {
    // Allow if user is owner
    if (isOwner) return true;

    // Allow if user is from agency and viewing client
    if (businessProfile?.parentCompany === userProfile?.companyId) {
      return true;
    }

    return false;
  };

  if (!canManageCompany()) {
    return (
      <div className="p-6 space-y-6 w-full">
        <div className="bg-yellow-100 w-full border-l-4 border-yellow-500 text-yellow-700 p-4" role="alert">
          <p className="font-bold">Access Restricted</p>
          <p>Only the account administrator or agency manager can manage company information.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" style={{fontFamily: '"Share Tech Mono", monospace'}}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-10">
          {/* Header Section */}
          <div className="border-b pb-6" style={{borderColor: 'rgba(241, 196, 15, 0.3)'}}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center mb-1">
                  <h2 className="text-base font-black leading-7 tracking-wide" style={{
                    color: '#F1C40F',
                    textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
                  }}>
                    COMPANY INFORMATION
                  </h2>
                  {unsavedChanges && !saveSuccess && (
                    <span className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold" style={{
                      background: 'rgba(231, 76, 60, 0.2)',
                      border: '1px solid rgba(231, 76, 60, 0.4)',
                      color: '#E74C3C'
                    }}>
                      UNSAVED CHANGES
                    </span>
                  )}
                  {saveSuccess && (
                    <span className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold" style={{
                      background: 'rgba(46, 204, 113, 0.2)',
                      border: '1px solid rgba(46, 204, 113, 0.4)',
                      color: '#2ECC71'
                    }}>
                      CHANGES SAVED
                    </span>
                  )}
                </div>
                <p className="text-sm leading-6 font-bold" style={{color: '#BDC3C7'}}>
                  Manage your company information. Header configuration is available in game editor.
                </p>
              </div>
              <button
                type="submit"
                disabled={!unsavedChanges || loading}
                className={`inline-flex justify-center rounded-lg px-6 py-3 text-sm font-bold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 min-w-[100px] transition-all duration-100 ${
                  unsavedChanges && !loading 
                    ? '' 
                    : 'cursor-not-allowed'
                }`}
                style={unsavedChanges && !loading ? {
                  background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                  border: '2px solid rgba(241, 196, 15, 0.6)',
                  color: '#FFFFFF',
                  textShadow: '1px 1px 0px #A93226'
                } : {
                  background: 'rgba(52, 73, 94, 0.6)',
                  border: '1px solid rgba(149, 165, 166, 0.3)',
                  color: '#95A5A6'
                }}
                onMouseEnter={(e) => {
                  if (unsavedChanges && !loading) {
                    e.target.style.background = 'linear-gradient(135deg, #C0392B, #A93226)';
                    e.target.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (unsavedChanges && !loading) {
                    e.target.style.background = 'linear-gradient(135deg, #E74C3C, #C0392B)';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                {loading ? (
                  <BeatLoader size={8} color="#ffffff" />
                ) : (
                  'SAVE'
                )}
              </button>
            </div>
          </div>

          {/* Basic Information Section */}
          <div className="rounded-xl overflow-hidden" style={{
            background: 'linear-gradient(135deg, #2C3E50, #34495E)',
            border: '2px solid rgba(241, 196, 15, 0.4)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}>
            <div className="px-6 py-5 border-b" style={{borderColor: 'rgba(241, 196, 15, 0.3)'}}>
              <h2 className="text-lg font-black tracking-wide" style={{
                color: '#F1C40F',
                textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
              }}>
                BASIC INFORMATION
              </h2>
              <p className="mt-1 text-sm font-bold" style={{color: '#BDC3C7'}}>
                This information helps us understand your business better and powers AI-driven suggestions.
              </p>
            </div>
            <div className="px-6 py-6 space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="block text-sm font-bold" style={{color: '#ECF0F1'}}>
                    BUSINESS NAME
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={options.name}
                    onChange={handleChange}
                    placeholder="Enter your business name"
                    className="block w-full rounded-lg shadow-sm sm:text-sm transition-all duration-200 font-bold"
                    style={{
                      background: 'rgba(52, 73, 94, 0.6)',
                      border: '1px solid rgba(241, 196, 15, 0.4)',
                      color: '#ECF0F1',
                      fontFamily: '"Share Tech Mono", monospace'
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2" style={{color: '#ECF0F1'}}>
                  ABOUT YOUR BUSINESS
                </label>
                <textarea
                  name="description"
                  value={options.description}
                  onChange={handleChange}
                  placeholder="e.g., We're a family-owned bakery specializing in artisanal breads and pastries..."
                  className="block w-full rounded-lg shadow-sm sm:text-sm transition-all duration-200 font-bold"
                  style={{
                    background: 'rgba(52, 73, 94, 0.6)',
                    border: '1px solid rgba(241, 196, 15, 0.4)',
                    color: '#ECF0F1',
                    fontFamily: '"Share Tech Mono", monospace'
                  }}
                  rows={4}
                />
                <p className="mt-2 text-sm font-bold" style={{color: '#95A5A6'}}>
                  Tell us about your business to help our AI provide more relevant and personalized suggestions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}